import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";

// CSV: scenario_code,scenario_name,description,shocks_json
// shocks_json is an inline JSON object, e.g. {"equity":-0.4,"credit":-0.15,"rates_bps":200}
export default function StressScenariosUploader() {
  const [csv, setCsv] = useState("");
  const [busy, setBusy] = useState(false);
  const [count, setCount] = useState<number>(0);
  const { toast } = useToast();

  const loadStats = async () => {
    const { count } = await supabase
      .from("policy_stress_scenarios" as any)
      .select("*", { count: "exact", head: true });
    setCount(count ?? 0);
  };
  useEffect(() => { loadStats(); }, []);

  const upload = async () => {
    setBusy(true);
    try {
      const lines = csv.trim().split(/\r?\n/).filter(l => l.trim());
      if (!lines.length) throw new Error("No rows.");
      const first = lines[0].toLowerCase();
      const data = first.includes("scenario_code") ? lines.slice(1) : lines;

      const rows = data.map(l => {
        // Allow JSON braces with commas — split only on first 3 commas
        const m = l.match(/^([^,]+),([^,]+),([^,]*),(.*)$/);
        if (!m) throw new Error(`Bad row: ${l}`);
        const [, code, name, desc, shocksRaw] = m;
        let shocks: any = {};
        try { shocks = shocksRaw.trim() ? JSON.parse(shocksRaw.trim()) : {}; }
        catch { throw new Error(`Invalid JSON in shocks: ${shocksRaw}`); }
        return {
          scenario_code: code.trim(),
          scenario_name: name.trim(),
          description: desc.trim() || null,
          shocks,
        };
      });

      const { error } = await supabase
        .from("policy_stress_scenarios" as any)
        .upsert(rows, { onConflict: "scenario_code" });
      if (error) throw error;

      toast({ title: "Upload complete", description: `${rows.length} scenarios upserted.` });
      setCsv("");
      loadStats();
    } catch (e: any) {
      toast({ title: "Upload failed", description: e.message ?? String(e), variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader><CardTitle>§13 Stress Scenarios</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="text-xs text-muted-foreground space-y-1">
          <p>Library of named market stress scenarios. Run results stored separately per portfolio per run date.</p>
          <p><strong>CSV:</strong> <code>scenario_code,scenario_name,description,shocks_json</code></p>
          <p><strong>shocks_json example:</strong> <code>{`{"equity":-0.4,"credit":-0.15,"rates_bps":200}`}</code></p>
        </div>
        <div className="text-xs border rounded p-3 bg-muted/30">
          <span className="text-muted-foreground">Scenarios in library:</span>{" "}
          <span className="font-medium tabular-nums">{count}</span>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Paste CSV</Label>
          <Textarea
            value={csv}
            onChange={(e) => setCsv(e.target.value)}
            placeholder={'scenario_code,scenario_name,description,shocks_json\nGFC2008,2008 Global Financial Crisis,Lehman peak-to-trough,{"equity":-0.45,"credit":-0.20}'}
            rows={8}
            className="font-mono text-xs"
          />
        </div>
        <div className="flex gap-2">
          <Button onClick={upload} disabled={busy || !csv.trim()}>{busy ? "Uploading…" : "Upload"}</Button>
          <Button variant="outline" onClick={() => setCsv("")} disabled={busy}>Clear</Button>
        </div>
      </CardContent>
    </Card>
  );
}
